import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { fetchMathConcepts as fetchMathConceptsUtil, getConceptOrder as getConceptOrderUtil, getConceptById as getConceptByIdUtil } from '../utils/mathConceptsUtils';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { Upload, FileText, Download, Trash2, Play, AlertCircle, Plus, Settings, CheckSquare, Square, Edit, UploadCloud, Eye, ChevronLeft, ChevronRight, FileX, Bot, Edit2, Save, X, Copy } from 'lucide-react';
import UploadModal from '../components/SchoolExamManagement/UploadModal';
import ObjectModal from '../components/SchoolExamManagement/ObjectModal';
import SingleFileUploadModal from '../components/SchoolExamManagement/SingleFileUploadModal';
import EditObjectModal from '../components/SchoolExamManagement/EditObjectModal';
import AnalysisResultTable from '../components/SchoolExamManagement/AnalysisResultTable';
import { convertToRelativeGrade } from '../utils/gradeUtils';
import SchoolNameUtils from '../utils/schoolNameUtils';
import schoolList from '../data/school_list.json';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Button, Modal, ConfirmModal, BypassLayout, MathceptDetailModal, useMathceptDetail } from '../components/common';
// 데스크톱 확장 추가
import { useDesktopExpanded } from '../hooks/useDesktopExpanded';
import '../styles/desktop-expanded.css';



function SchoolExamManagement() {
  const { tab, page_number } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(tab || 'analysis');
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken, userType } = useAuth();
  const [mathConcepts, setMathConcepts] = useState([]);
  const [isLoadingMathConcepts, setIsLoadingMathConcepts] = useState(false);
  
  // 데스크톱 확장 훅 사용
  const {
    getComponentClasses,
    shouldUsePagination
  } = useDesktopExpanded({
    expandConfig: {
      mode: 'grid',
      maintainMobileWidth: true
    }
  });



  // 모달 상태
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showObjectModal, setShowObjectModal] = useState(false);
  const [showSingleUploadModal, setShowSingleUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExamForUpload, setSelectedExamForUpload] = useState(null);
  const [selectedExamForEdit, setSelectedExamForEdit] = useState(null);
  const [selectedExamDetail, setSelectedExamDetail] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isIOS, setIsIOS] = useState(false);
  
  // 매스컨셉 상세 정보 모달 (공통 컴포넌트 사용)
  const { isModalOpen: mathceptDetailModalOpen, loading: mathceptDetailLoading, data: mathceptDetailData, fetchAndShow: fetchMathceptDetails, closeModal: closeMathceptDetailModal } = useMathceptDetail();
  
  // 문제 편집 상태
  const [editingProblem, setEditingProblem] = useState(null);
  const [editingConceptIds, setEditingConceptIds] = useState([]);
  const [editingDifficulty, setEditingDifficulty] = useState(1);
  const [isLoadingConceptList, setIsLoadingConceptList] = useState(false);
  const [allMathConcepts, setAllMathConcepts] = useState([]);
  const [showConceptSelector, setShowConceptSelector] = useState(false);
  const [conceptSearchTerm, setConceptSearchTerm] = useState('');
  const [conceptSelectorPosition, setConceptSelectorPosition] = useState({ top: 0, left: 0 });

  // 대량 작업 상태
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedExams, setSelectedExams] = useState([]);

  // 데이터 탭 상태
  const [examData, setExamData] = useState([]);
  const [page, setPage] = useState(parseInt(page_number) || 1);
  const [pageInputValue, setPageInputValue] = useState(String(parseInt(page_number) || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const searchParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    school_name: searchParams.get('school_name') || '',
    att_year: searchParams.get('att_year') || '',
    school_grade: searchParams.get('school_grade') || '',
    phase: searchParams.get('phase') || '',
    subject: searchParams.get('subject') || '',
    file_exists: searchParams.get('file_exists') || ''
  });
  const [isFilterMode, setIsFilterMode] = useState(
    searchParams.get('school_name') || 
    searchParams.get('att_year') || 
    searchParams.get('school_grade') || 
    searchParams.get('phase') || 
    searchParams.get('subject') || 
    searchParams.get('file_exists')
  );


  const phases = [
    { value: '11', label: '1학기 중간' },
    { value: '12', label: '1학기 기말' },
    { value: '13', label: '1학기 기타' },
    { value: '21', label: '2학기 중간' },
    { value: '22', label: '2학기 기말' },
    { value: '23', label: '2학기 기타' }
  ];

  // 학년 표시를 위한 헬퍼 함수
  const formatGradeDisplay = (absoluteGrade) => {
    const gradeInfo = convertToRelativeGrade(absoluteGrade);
    if (gradeInfo.schoolType === 1) {
      return `${gradeInfo.relativeGrade}학년 (초등)`;
    } else if (gradeInfo.schoolType === 2) {
      return `${gradeInfo.relativeGrade}학년 (중등)`;
    } else if (gradeInfo.schoolType === 3) {
      return `${gradeInfo.relativeGrade}학년 (고등)`;
    }
    return `${absoluteGrade}학년`;
  };

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = () => {
    try {
      // 전체 화면 높이에서 고정 영역들을 제외
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      
      // 실제 모바일 기기인지 데스크톱에서 축소한 것인지 판단
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isDesktopResponsive = isMobileView && !isMobileDevice && windowWidth >= 375; // 데스크톱에서 모바일 뷰로 전환
      
      let fixedHeight, tableHeader, paginationArea, margins, rowHeight;
      
      if (isDesktopResponsive) {
        // 데스크톱에서 모바일 반응형으로 전환된 경우
        // 탭(48) + 헤더&필터(약 120) = 168px 추가
        fixedHeight = 20 * 16; // 10.5rem (168px)
        tableHeader = 35; // 테이블 헤더도 줄임
        paginationArea = 25; // 페이지네이션 더 줄임
        margins = 50; //임의로 맞춤
        rowHeight = 45; // 시험 데이터는 학생보다 조금 작게
      } else if (isMobileView) {
        // 실제 모바일 기기
        // 기존 10rem + 탭(3rem) + 추가필터(2rem) = 15rem
        fixedHeight = 20 * 16; // 15rem (240px)
        tableHeader = 40;
        paginationArea = 40;
        margins = 70; //임의로 맞춤
        rowHeight = 43;
      } else {
        // 데스크톱
        // 기존 14rem + 탭(3.5rem) + 추가필터(2.5rem) = 20rem
        fixedHeight = 20 * 16; // 20rem (320px)
        tableHeader = 48;
        paginationArea = 72;
        margins = 70 // 임의로 맞춤;
        rowHeight = 40; // 데스크톱에서는 더 작게
      }
      
      const availableHeight = windowHeight - fixedHeight - tableHeader - paginationArea - margins;
      const itemsCount = Math.floor(availableHeight / rowHeight);
      
      console.log(`${isDesktopResponsive ? 'Desktop-Responsive' : (isMobileView ? 'Mobile' : 'Desktop')} calculation:`, {
        windowHeight,
        windowWidth,
        fixedHeight, 
        availableHeight, 
        rowHeight,
        itemsCount,
        isMobileDevice,
        isDesktopResponsive
      });
      
      // 최소/최대 개수 제한
      const minItems = isMobileView ? 5 : 5;
      const maxItems = isMobileView ? 30 : 50;
      
      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 20; // 기본값
    }
  };

  // 학교명 축약 표시 함수
  const getDisplaySchoolName = (schoolName) => {
    // school_list.json에서 매칭되는 학교 찾기
    const matchedSchool = schoolList.find(school => school.school_name === schoolName);
    
    if (matchedSchool) {
      // 매칭된 학교가 있으면 축약 규칙 적용
      return SchoolNameUtils.getAbbreviation(matchedSchool.school_name, matchedSchool.school_level);
    }
    
    // 매칭되지 않으면 원본 그대로 반환 (문제집 등)
    return schoolName;
  };

  // 데이터 조회
  const fetchExamData = async (pageNum = 1, useFilter = false) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const itemsPerPage = calculateItemsPerPage();
      const params = new URLSearchParams({
        page: pageNum,
        per_page: itemsPerPage
      });

      if (useFilter) {
        if (filters.school_name) params.append('school_name', filters.school_name);
        if (filters.att_year) params.append('att_year', filters.att_year);
        if (filters.school_grade) params.append('school_grade', filters.school_grade);
        if (filters.phase) params.append('phase', filters.phase);
        if (filters.subject) params.append('subject', filters.subject);
        if (filters.file_exists && (filters.file_exists === 'true' || filters.file_exists === 'false')) {
          params.append('file_exists', filters.file_exists);
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/list?${params}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }
      
      if (response.status === 403) {
        // 403 Forbidden - 권한 문제
        // Teacher에 대한 권한 문제를 백엔드에서 해결할 때까지 임시 처리
        if (userType === 'teacher') {
          alert('현재 시험분석 기능의 강사 권한이 제한되어 있습니다. 시스템 관리자에게 문의하세요.');
        } else {
          alert('시험분석 기능 접근 권한이 없습니다.');
        }
        setExamData([]);
        setTotalPages(1);
        setPage(1);
        setTotalItems(0);
        return;
      }

      if (response.status === 404) {
        setExamData([]);
        setTotalPages(1);
        setPage(1);
        return;
      }

      if (!response.ok) {
        throw new Error('시험 데이터를 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setExamData(result.data);
      setTotalPages(result.total_pages || 1);
      setPage(pageNum);
      setTotalItems(result.total_items || result.total_count || 0);
    } catch (error) {
      console.error('Error fetching exam data:', error);
      setExamData([]);
      setTotalPages(1);
      setPage(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  // URL 업데이트 함수
  const updateURL = (pageNum, filterData, tabName = activeTab) => {
    const params = new URLSearchParams();
    
    if (filterData.school_name) params.append('school_name', filterData.school_name);
    if (filterData.att_year) params.append('att_year', filterData.att_year);
    if (filterData.school_grade) params.append('school_grade', filterData.school_grade);
    if (filterData.phase) params.append('phase', filterData.phase);
    if (filterData.subject) params.append('subject', filterData.subject);
    if (filterData.file_exists) params.append('file_exists', filterData.file_exists);
    
    const queryString = params.toString();
    navigate(`/school-exams/${tabName}/${pageNum}${queryString ? `?${queryString}` : ''}`);
  };

  // 탭 변경 함수
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'data') {
      updateURL(page, filters, newTab);
    } else {
      // 분석 탭은 페이지 번호가 필요 없으므로 1로 설정
      navigate(`/school-exams/${newTab}/1`);
    }
  };

  // 대량 작업 토글
  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedExams([]);
  };

  // 체크박스 선택/해제
  const handleExamSelect = (sxmId) => {
    setSelectedExams(prev => 
      prev.includes(sxmId) 
        ? prev.filter(id => id !== sxmId)
        : [...prev, sxmId]
    );
  };

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedExams.length === examData.length) {
      setSelectedExams([]);
    } else {
      setSelectedExams(examData.map(exam => exam.sxm_id));
    }
  };

  // 분석 상태 표시를 위한 헬퍼 함수
  const getAnalysisStatusDisplay = (analysis) => {
    switch (analysis) {
      case 'empty':
        return <span className="text-gray-600">미분석</span>;
      case 'waiting':
        return <span className="text-blue-600">분석 중</span>;
      case 'completed':
        return <span className="text-success">완료</span>;
      case 'outdated':
        return <span className="text-orange-600">업데이트 필요</span>;
      case 'error':
        return <span className="text-danger">오류</span>;
      default:
        return <span className="text-gray-600">미분석</span>;
    }
  };

  // 분석 요청 가능 여부 확인
  const canRequestAnalysis = (analysis) => {
    return analysis === 'empty' || analysis === 'completed' || analysis === 'error';
  };

  // 객체 삭제 가능 여부 확인 (분석 중일 때만 불가)
  const canDeleteObject = (analysis) => {
    return analysis !== 'waiting';
  };

  // 대량 분석 시작
  const handleBulkAnalyze = async () => {
    if (selectedExams.length === 0) {
      alert('분석할 시험을 선택해주세요.');
      return;
    }

    // 분석 요청 가능한 시험들만 필터링
    const validExams = selectedExams.filter(sxmId => {
      const exam = examData.find(e => e.sxm_id === sxmId);
      return exam && canRequestAnalysis(exam.analysis);
    });

    if (validExams.length === 0) {
      alert('분석 요청 가능한 시험이 없습니다. (분석 중이거나 업데이트 필요한 상태는 제외됩니다)');
      return;
    }

    if (validExams.length !== selectedExams.length) {
      const excludedCount = selectedExams.length - validExams.length;
      if (!window.confirm(`선택된 ${selectedExams.length}개 중 ${validExams.length}개만 분석 가능합니다.\n(${excludedCount}개는 이미 분석 중이거나 업데이트 필요 상태)\n\n계속하시겠습니까?`)) {
        return;
      }
    }

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/bulk-close`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sxm_ids: validExams
          })
        }
      );

      if (!response.ok) {
        throw new Error('대량 분석 시작에 실패했습니다.');
      }

      const result = await response.json();
      alert(`대량 분석이 시작되었습니다. 처리된 항목: ${result.data.total_processed}개`);
      setSelectedExams([]);
      fetchExamData(page, isFilterMode);
    } catch (error) {
      console.error('Error starting bulk analysis:', error);
      alert(error.message);
    }
  };

  // 시험 객체 삭제 (파일 삭제와 구분)
  const handleObjectDelete = async (sxmId, examInfo) => {
    if (!window.confirm(`시험 객체를 완전히 삭제하시겠습니까?\n학교: ${examInfo.school_name}\n학년: ${formatGradeDisplay(examInfo.school_grade)}\n시험: ${phases.find(p => p.value == examInfo.phase)?.label}\n과목: ${examInfo.subject || '없음'}\n\n※ 파일과 분석 결과도 함께 삭제됩니다.`)) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${sxmId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('시험 객체 삭제에 실패했습니다.');
      }

      alert('시험 객체가 삭제되었습니다.');
      fetchExamData(page, Object.values(filters).some(f => f !== ''));
    } catch (error) {
      console.error('Error deleting exam object:', error);
      alert(error.message);
    }
  };

  // 업로드 성공 후 콜백
  const handleUploadSuccess = () => {
    fetchExamData(page, isFilterMode);
  };

  // 객체 생성/수정 성공 후 콜백
  const handleObjectSuccess = () => {
    fetchExamData(page, isFilterMode);
  };

  // 검색 핸들러
  const handleSearch = () => {
    setIsFilterMode(true);
    updateURL(1, filters);
    fetchExamData(1, true);
  };

  // 초기화 핸들러
  const handleReset = () => {
    setIsFilterMode(false);
    setFilters({
      school_name: '',
      att_year: '',
      school_grade: '',
      phase: '',
      subject: '',
      file_exists: ''
    });
    navigate(`/school-exams/${activeTab}/1`);
    fetchExamData(1, false);
  };

  // 페이지 변경
  const handlePageChange = (newPage) => {
    updateURL(newPage, filters);
    fetchExamData(newPage, isFilterMode);
  };

  // 필터 변경
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // 엔터키 처리 함수
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };



  // 파일 삭제
  const handleFileDelete = async (sxmId) => {
    if (!window.confirm('파일을 삭제하시겠습니까?')) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${sxmId}/file-delete`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('파일 삭제에 실패했습니다.');
      }

      alert('파일이 삭제되었습니다.');
      fetchExamData(page, Object.values(filters).some(f => f !== ''));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(error.message);
    }
  };

  // 분석 시작
  const handleAnalyze = async (sxmId, examInfo = null) => {
    // 확인창 표시
    const examInfoText = examInfo ? 
      `\n시험: ${examInfo.school_name} ${formatGradeDisplay(examInfo.school_grade)} ${phases.find(p => p.value == examInfo.phase)?.label} ${examInfo.subject || ''}` : 
      `\nID: ${sxmId}`;
      
    if (!window.confirm(`분석을 시작하시겠습니까?${examInfoText}\n\n※ 분석에는 시간이 소요될 수 있습니다.`)) {
      return;
    }

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${sxmId}/close`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('분석 시작에 실패했습니다.');
      }

      alert('분석이 시작되었습니다.');
      fetchExamData(page, Object.values(filters).some(f => f !== ''));
    } catch (error) {
      console.error('Error starting analysis:', error);
      alert(error.message);
    }
  };

  // 단일 파일 업로드 모달 열기
  const handleSingleUpload = (exam) => {
    setSelectedExamForUpload(exam);
    setShowSingleUploadModal(true);
  };

  // 객체 수정 모달 열기
  const handleEditObject = (exam) => {
    setSelectedExamForEdit(exam);
    setShowEditModal(true);
  };

  // 단일 파일 업로드 성공 후 콜백
  const handleSingleUploadSuccess = () => {
    fetchExamData(page, Object.values(filters).some(f => f !== ''));
    setSelectedExamForUpload(null);
  };

  // 객체 수정 성공 후 콜백
  const handleEditSuccess = () => {
    fetchExamData(page, Object.values(filters).some(f => f !== ''));
    setSelectedExamForEdit(null);
  };

  // 수학 개념 목록 조회 (공유 Context 사용)
  const fetchMathConcepts = async () => {
    // Context에서 데이터가 이미 있으면 바로 반환
    if (mathConcepts.length > 0) {
      console.log('Using cached math concepts from context:', mathConcepts.length, 'items');
      return mathConcepts;
    }
    
    // 없으면 유틸리티 함수를 통해 로드
    console.log('Loading math concepts from utility...');
    const concepts = await fetchMathConceptsUtil();
    return concepts || [];
  };

  // LaTeX 수식 렌더링 함수
  const renderLatex = (text) => {
    try {
      // $...$ 패턴을 찾아서 LaTeX로 렌더링
      return text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, {
            throwOnError: false,
            displayMode: false
          });
        } catch (e) {
          return match; // 렌더링 실패 시 원본 반환
        }
      });
    } catch (error) {
      return text; // 전체 처리 실패 시 원본 반환
    }
  };

  // 개념 ID를 개념명으로 변환 (Context의 데이터 사용)
  const getConceptText = (conceptId, concepts = mathConcepts) => {
    const concept = concepts.find(c => c.id === parseInt(conceptId));
    if (!concept) {
      console.log(`Concept not found for ID ${conceptId}. Available concepts count:`, concepts.length);
      if (concepts.length > 0) {
        console.log('Sample concepts:', concepts.slice(0, 3));
      }
    }
    return concept ? concept.text : `개념 ${conceptId}`;
  };
  
  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      setIsLoadingMathConcepts(true);
      try {
        const concepts = await fetchMathConceptsUtil();
        setMathConcepts(concepts);
      } catch (error) {
        console.error('Error loading math concepts:', error);
      } finally {
        setIsLoadingMathConcepts(false);
      }
    };
    
    loadMathConcepts();
  }, []);

  // Helper 함수들을 로컬 mathConcepts를 사용하도록 수정
  const getConceptOrder = (conceptId) => getConceptOrderUtil(mathConcepts, conceptId);
  const getConceptById = (conceptId) => getConceptByIdUtil(mathConcepts, conceptId);



  // 전체 수학 개념 목록 로드
  const loadAllMathConcepts = async () => {
    if (allMathConcepts.length > 0) return; // 이미 로드된 경우 스킵
    
    try {
      setIsLoadingConceptList(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mathcepts/temp`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setAllMathConcepts(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading math concepts:', error);
    } finally {
      setIsLoadingConceptList(false);
    }
  };

  // 문제 편집 시작
  const startEditProblem = (problem) => {
    setEditingProblem(problem.problem_number);
    setEditingConceptIds(problem.matched_concept_ids || []);
    setEditingDifficulty(problem.difficulty || 1);
    setShowConceptSelector(false);
    setConceptSearchTerm('');
    loadAllMathConcepts(); // 개념 목록 로드
  };

  // 문제 편집 취소
  const cancelEditProblem = () => {
    setEditingProblem(null);
    setEditingConceptIds([]);
    setEditingDifficulty(1);
    setShowConceptSelector(false);
    setConceptSearchTerm('');
  };

  // 문제 분석 결과 저장
  // API 사양:
  // - problem_number에 해당하는 문제가 있으면 수정, 없으면 생성
  // - matched_concept_ids가 빈 배열이면 해당 문제 삭제
  // TODO: 향후 기능 추가 시
  // - 새 문제 추가 버튼 (현재는 기존 문제만 수정 가능)
  // - 문제 삭제 버튼 (현재는 matched_concept_ids를 빈 배열로 보내야 함)
  const saveProblemAnalysis = async (sxmId, problemNumber) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 현재는 빈 배열 전송을 막음 (삭제 기능 비활성화)
      if (editingConceptIds.length === 0) {
        alert('최소 1개 이상의 개념을 선택해야 합니다.');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${sxmId}/analysis`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            problem_number: problemNumber,
            matched_concept_ids: editingConceptIds,
            difficulty: editingDifficulty
          })
        }
      );

      if (response.status === 400) {
        const error = await response.json();
        alert('요청 데이터가 올바르지 않습니다: ' + (error.message || ''));
        return;
      }

      if (response.status === 404) {
        alert('시험 또는 분석 결과를 찾을 수 없습니다.');
        return;
      }

      if (response.status === 409) {
        alert('해당 시험이 분석 중이거나 대기 중입니다. 분석이 완료된 후 다시 시도해주세요.');
        return;
      }

      if (!response.ok) {
        throw new Error('분석 결과 저장에 실패했습니다.');
      }

      const result = await response.json();
      alert('분석 결과가 성공적으로 저장되었습니다.');
      
      // 상세 정보 다시 로드
      handleViewDetail({ sxm_id: sxmId, file_exists: selectedExamDetail.file_exists });
      cancelEditProblem();
    } catch (error) {
      console.error('Error saving problem analysis:', error);
      alert(error.message);
    }
  };

  // 개념 선택/해제
  const toggleConceptSelection = (conceptId) => {
    setEditingConceptIds(prev => {
      if (prev.includes(conceptId)) {
        return prev.filter(id => id !== conceptId);
      } else {
        return [...prev, conceptId];
      }
    });
  };

  // 테이블 데이터를 엑셀로 복사
  const copyTableToExcel = (analysisData) => {
    try {
      let excelData = [];
      
      // 헤더 추가 (4개 컬럼)
      excelData.push(['문제', '난이도', '매칭된 개념', '']);
      
      // 데이터 처리
      analysisData.forEach(problem => {
        const problemNumber = problem.problem_number || '-';
        const difficulty = problem.difficulty || '-';
        
        if (Array.isArray(problem.matched_concept_ids) && problem.matched_concept_ids.length > 0) {
          // 매칭된 개념이 있는 경우
          problem.matched_concept_ids.forEach((conceptId, index) => {
            const conceptText = getConceptText(conceptId);
            const conceptData = getConceptById(conceptId);
            
            // LaTeX 수식 제거하고 일반 텍스트로 변환
            const cleanText = conceptText
              .replace(/\$\$(.*?)\$\$/g, '$1')  // $$...$$ 제거
              .replace(/\$(.*?)\$/g, '$1')      // $...$ 제거
              .replace(/\\[a-zA-Z]+{([^}]*)}/g, '$1')  // \command{...} -> ...
              .replace(/\\[a-zA-Z]+/g, '')      // \command 제거
              .replace(/[{}]/g, '');            // {} 제거
            
            // 개념 타입을 한글로 변환
            let conceptTypeKorean = '';
            if (conceptData && conceptData.concept_type) {
              const typeMap = {
                'DEF': '정의',
                'AXM': '공리',
                'IDN': '정리',
                'THM': '이론',
                'MTH': '방법론',
                'PRP': '성질',
                'FOR': '공식',
                'NTN': '표기법'
              };
              conceptTypeKorean = typeMap[conceptData.concept_type] || conceptData.concept_type;
            }
            
            if (index === 0) {
              // 첫 번째 개념: 문제 번호와 난이도 포함
              excelData.push([problemNumber, difficulty, conceptTypeKorean, cleanText]);
            } else {
              // 추가 개념: 문제 번호와 난이도 비워두고 개념 타입과 내용만 표시
              excelData.push(['', '', conceptTypeKorean, cleanText]);
            }
          });
        } else {
          // 매칭된 개념이 없는 경우
          excelData.push([problemNumber, difficulty, '', '매칭된 개념 없음']);
        }
      });
      
      // 탭으로 구분된 문자열로 변환 (엑셀에 붙여넣기 위함)
      const tsvContent = excelData.map(row => row.join('\t')).join('\n');
      
      // 클립보드에 복사
      navigator.clipboard.writeText(tsvContent).then(() => {
        alert('테이블이 클립보드에 복사되었습니다.\n엑셀에서 붙여넣기(Ctrl+V 또는 Cmd+V)하세요.');
      }).catch(err => {
        console.error('클립보드 복사 실패:', err);
        
        // 대체 방법: textarea 사용
        const textarea = document.createElement('textarea');
        textarea.value = tsvContent;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        
        alert('테이블이 클립보드에 복사되었습니다.\n엑셀에서 붙여넣기(Ctrl+V 또는 Cmd+V)하세요.');
      });
    } catch (error) {
      console.error('테이블 복사 중 오류:', error);
      alert('복사 중 오류가 발생했습니다.');
    }
  };

  // 시험 상세 정보 조회 (PDF 미리보기와 통합)
  const handleViewDetail = async (exam) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 수학 개념 목록이 없으면 먼저 로드
      let concepts = mathConcepts;
      if (concepts.length === 0) {
        concepts = await fetchMathConcepts();
      }

      // 1. 시험 상세 정보 조회
      console.log('Fetching exam detail for ID:', exam.sxm_id);
      const detailResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${exam.sxm_id}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (detailResponse.status === 401) {
        navigate('/signin');
        return;
      }

      if (!detailResponse.ok) {
        console.error('Detail response error:', detailResponse.status, detailResponse.statusText);
        throw new Error(`시험 상세 정보를 불러오는데 실패했습니다. (${detailResponse.status})`);
      }

      const detailResult = await detailResponse.json();
      console.log('Exam Detail API Response:', detailResult);

      // 2. 파일이 있으면 다운로드 URL도 조회
      let fileUrl = null;
      let fileError = null;
      
      if (exam.file_exists) {
        try {
          console.log('Fetching file URL for exam:', exam.sxm_id);
          const fileResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${exam.sxm_id}/file-download-url`,
            {
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json',
              }
            }
          );

          console.log('File URL response status:', fileResponse.status);
          
          if (fileResponse.ok) {
            const fileResult = await fileResponse.json();
            console.log('File URL API Response:', fileResult);
            fileUrl = fileResult.data.download_url;
            console.log('Retrieved file URL:', fileUrl);
          } else {
            console.error('File URL response error:', fileResponse.status, fileResponse.statusText);
            const errorText = await fileResponse.text();
            console.error('Error response body:', errorText);
            fileError = `파일 URL 조회 실패 (${fileResponse.status}): ${errorText}`;
          }
        } catch (fileError_) {
          console.error('Error fetching file URL:', fileError_);
          fileError = `파일 URL 조회 중 오류: ${fileError_.message}`;
        }
      }
      
      setSelectedExamDetail({
        ...detailResult.data,
        fileUrl: fileUrl,
        fileError: fileError
      });
      
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching exam detail:', error);
      alert(error.message);
    }
  };



  // 화면 크기 변경 감지 및 iOS 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    // iOS 감지
    const detectIOS = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    };
    
    setIsIOS(detectIOS());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 화면 크기 변경 시 per_page 재계산
  useEffect(() => {
    const handleResize = () => {
      // 화면 크기가 변경되면 데이터를 다시 불러옴
      if (examData.length > 0) {
        fetchExamData(page, isFilterMode);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [page, isFilterMode, isMobileView]);

  // page 변경 시 pageInputValue 동기화
  useEffect(() => {
    setPageInputValue(String(page));
  }, [page]);

  // Safe area 감지
  const [hasSafeArea, setHasSafeArea] = useState(false);
  useEffect(() => {
    if (isIOS && isMobileView) {
      // iOS에서 safe area 존재 여부 감지 (notch가 있는 기기)
      const checkSafeArea = () => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.clientHeight;
        const hasNotch = windowHeight > 800 || window.screen.height > 800; // iPhone X 이상
        setHasSafeArea(hasNotch);
      };
      
      checkSafeArea();
      window.addEventListener('orientationchange', checkSafeArea);
      return () => window.removeEventListener('orientationchange', checkSafeArea);
    }
  }, [isIOS, isMobileView]);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin', { replace: true });
        return;
      }
      
      // 탭 설정
      if (tab) {
        setActiveTab(tab);
      }
      
      // 데이터 탭일 때만 데이터 로드
      if (tab === 'data') {
        const pageNum = parseInt(page_number) || 1;
        setPage(pageNum);
        
        const hasFilters = searchParams.toString().length > 0;
        setIsFilterMode(hasFilters);
        
        setFilters({
          school_name: searchParams.get('school_name') || '',
          att_year: searchParams.get('att_year') || '',
          school_grade: searchParams.get('school_grade') || '',
          phase: searchParams.get('phase') || '',
          subject: searchParams.get('subject') || '',
          file_exists: searchParams.get('file_exists') || ''
        });
        
        fetchExamData(pageNum, hasFilters);
      }
      
      // 수학 개념은 Context에서 자동으로 로드됨 (중복 호출 방지)
    };

    checkAuthAndFetch();
  }, [tab, page_number, location.search]);

  const renderPageNumbersForMobile = () => {
    if (isMobileView) {
      const pageNumbers = [];
      const maxVisible = 5;
      const halfVisible = Math.floor(maxVisible / 2);
      
      let startPage = Math.max(1, page - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisible - 1);
      
      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }
      
      const getPageNumbers = () => {
        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
        return pages;
      };

      // 첫 페이지
      if (getPageNumbers()[0] > 1) {
        pageNumbers.push(
          <button
            key={1}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        );
        if (getPageNumbers()[0] > 2) {
          pageNumbers.push(<span key="ellipsis1" className="px-1 text-gray-400">...</span>);
        }
      }

      // 중간 페이지들
      getPageNumbers().forEach(pageNum => {
        if (page === pageNum) {
          // 현재 페이지 - 인풋 박스로 렌더링
          const inputWidth = Math.max(2, String(pageInputValue).length * 0.6 + 1.2);
          pageNumbers.push(
            <input
              key={pageNum}
              type="text"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setPageInputValue(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newPage = parseInt(pageInputValue);
                  if (newPage >= 1 && newPage <= totalPages) {
                    e.target.blur();
                    setTimeout(() => {
                      handlePageChange(newPage);
                    }, 100);
                  } else {
                    setPageInputValue(String(page));
                  }
                }
              }}
              onMouseUp={(e) => e.preventDefault()}
              onFocus={(e) => e.target.select()}
              onBlur={() => setPageInputValue(String(page))}
              className="flex items-center justify-center rounded-full border border-primary bg-primary text-white text-xs font-bold"
              style={{ width: `${inputWidth}rem`, minWidth: '2rem', height: '2rem', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 4px' }}
            />
          );
        } else {
          pageNumbers.push(
            <button
              key={pageNum}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </button>
          );
        }
      });

      // 마지막 페이지
      if (getPageNumbers()[getPageNumbers().length - 1] < totalPages) {
        if (getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1) {
          pageNumbers.push(<span key="ellipsis2" className="px-1 text-gray-400">...</span>);
        }
        pageNumbers.push(
          <button
            key={totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }

      return pageNumbers;
    }
    return renderPageNumbers();
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);
    
    let startPage = Math.max(1, page - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    // 시작 페이지 조정
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    // 페이지 번호 배열 생성
    const getPageNumbers = () => {
      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    // 첫 페이지
    if (getPageNumbers()[0] > 1) {
      pageNumbers.push(
        <button
          key={1}
          className="page-button"
          style={{ borderRadius: '1.5rem' }}
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (getPageNumbers()[0] > 2) {
        pageNumbers.push(<span key="ellipsis1" className="page-dots">...</span>);
      }
    }

    // 중간 페이지들
    getPageNumbers().forEach(pageNum => {
      if (page === pageNum) {
        // 현재 페이지 - 인풋 박스로 렌더링
        const inputWidth = Math.max(2.5, String(pageInputValue).length * 0.6 + 1.5);
        pageNumbers.push(
          <input
            key={pageNum}
            type="text"
            value={pageInputValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setPageInputValue(value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newPage = parseInt(pageInputValue);
                if (newPage >= 1 && newPage <= totalPages) {
                  e.target.blur();
                  setTimeout(() => {
                    handlePageChange(newPage);
                  }, 100);
                } else {
                  setPageInputValue(String(page));
                }
              }
            }}
            onMouseUp={(e) => e.preventDefault()}
            onFocus={(e) => e.target.select()}
            onBlur={() => setPageInputValue(String(page))}
            className="page-button active font-bold"
            style={{
              borderRadius: '1.5rem',
              width: `${inputWidth}rem`,
              minWidth: '2.5rem',
              height: '40px',
              textAlign: 'center',
              outline: 'none',
              border: 'none',
              cursor: 'text',
              padding: '0 8px'
            }}
            aria-label="페이지 번호 입력"
          />
        );
      } else {
        pageNumbers.push(
          <button
            key={pageNum}
            className="page-button"
            style={{ borderRadius: '1.5rem' }}
            onClick={() => handlePageChange(pageNum)}
            aria-label={`${pageNum} 페이지`}
          >
            {pageNum}
          </button>
        );
      }
    });

    // 마지막 페이지
    if (getPageNumbers()[getPageNumbers().length - 1] < totalPages) {
      if (getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2" className="page-dots">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          className="page-button"
          style={{ borderRadius: '1.5rem' }}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };

  return (
    <BypassLayout
      loading={isLoading && examData.length === 0}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col ${isMobileView ? 'gap-4' : ''} ${
            isMobileView 
              ? 'h-full overflow-y-auto bg-gray-100' 
              : 'h-[calc(100vh-2rem-50px)]'
          }`}
          style={isMobileView ? getSafeAreaStyle() : {}}
        >
          {/* 네비게이션 - 데스크톱만 표시 */}
          {!isMobileView && (
            <nav className="shrink-0">
            </nav>
          )}

          <div className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1' : 'flex-1'
          }`}>
        {/* 탭 헤더 */}
        <div className="shrink-0 border-b">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full">
              <button
                onClick={() => handleTabChange('analysis')}
                className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                  activeTab === 'analysis'
                    ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                    : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                }`}
              >
                <Play className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                분석 결과
              </button>
              <button
                onClick={() => handleTabChange('data')}
                className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                  activeTab === 'data'
                    ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                    : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                }`}
              >
                <FileText className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                데이터 관리
              </button>
            </div>

          </div>
        </div>

        {/* 데이터 관리 탭 */}
        {activeTab === 'data' && (
          <div className="flex w-full flex-1 flex-col overflow-hidden">
            {/* 헤더 영역 - 학습분석 스타일 통일 */}
            <div className="shrink-0 border-b bg-white">
              <div className="p-4">
                <div className="mb-4 flex items-center gap-3">
                  <h3 className="text-lg font-semibold">시험 데이터 목록</h3>
                  <p className="text-xs text-gray-500">
                    - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>개 중 <span className="font-semibold text-primary">{examData?.length || 0}</span>개 표시 중
                  </p>
                </div>
                
                {/* 필터 및 액션 버튼 */}
                <div className="space-y-4">
                  {/* 필터와 초기화/조회 버튼 */}
                  <div className="grid grid-cols-4 gap-2">
                    {/* 첫 번째 행 */}
                    <input
                      type="text"
                      name="school_name"
                      placeholder="학교명 검색"
                      value={filters.school_name}
                      onChange={handleFilterChange}
                      onKeyPress={handleKeyPress}
                      className={`rounded border p-2 ${isMobileView ? 'text-xs' : 'text-sm'}`}
                      style={{ height: '36px' }}
                    />
                    
                    <input
                      type="number"
                      name="att_year"
                      placeholder="출제년도"
                      value={filters.att_year}
                      onChange={handleFilterChange}
                      onKeyPress={handleKeyPress}
                      className={`rounded border p-2 ${isMobileView ? 'text-xs' : 'text-sm'}`}
                      style={{ height: '36px' }}
                    />
                    
                    <select
                      name="school_grade"
                      value={filters.school_grade}
                      onChange={handleFilterChange}
                      className={`rounded border p-2 ${isMobileView ? 'text-xs' : 'text-sm'}`}
                      style={{ height: '36px' }}
                    >
                      <option value="">학년 선택</option>
                      <option value="1">1학년 (초등)</option>
                      <option value="2">2학년 (초등)</option>
                      <option value="3">3학년 (초등)</option>
                      <option value="4">4학년 (초등)</option>
                      <option value="5">5학년 (초등)</option>
                      <option value="6">6학년 (초등)</option>
                      <option value="7">1학년 (중등)</option>
                      <option value="8">2학년 (중등)</option>
                      <option value="9">3학년 (중등)</option>
                      <option value="10">1학년 (고등)</option>
                      <option value="11">2학년 (고등)</option>
                      <option value="12">3학년 (고등)</option>
                    </select>
                    
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      size="sm"
                      rounded="3xl"
                      style={{ height: '36px' }}
                    >
                      초기화
                    </Button>
                    
                    {/* 두 번째 행 */}
                    <select
                      name="phase"
                      value={filters.phase}
                      onChange={handleFilterChange}
                      className={`rounded border p-2 ${isMobileView ? 'text-xs' : 'text-sm'}`}
                      style={{ height: '36px' }}
                    >
                      <option value="">시험구분</option>
                      {phases.map(phase => (
                        <option key={phase.value} value={phase.value}>{phase.label}</option>
                      ))}
                    </select>
                    
                    <input
                      type="text"
                      name="subject"
                      placeholder="과목"
                      value={filters.subject}
                      onChange={handleFilterChange}
                      onKeyPress={handleKeyPress}
                      className={`rounded border p-2 ${isMobileView ? 'text-xs' : 'text-sm'}`}
                      style={{ height: '36px' }}
                    />
                    
                    <select
                      name="file_exists"
                      value={filters.file_exists}
                      onChange={handleFilterChange}
                      className={`rounded border p-2 ${isMobileView ? 'text-xs' : 'text-sm'}`}
                      style={{ height: '36px' }}
                    >
                      <option value="">파일 상태</option>
                      <option value="true">파일 있음</option>
                      <option value="false">파일 없음</option>
                    </select>
                    
                    <Button
                      variant="primary"
                      onClick={handleSearch}
                      size="sm"
                      rounded="3xl"
                      className="bg-headerBg hover:bg-gray-700"
                      style={{ height: '36px' }}
                    >
                      조회
                    </Button>
                  </div>
                  
                  {/* 액션 버튼들 */}
                  <div className="flex gap-3">
                    <Button
                      variant="primary"
                      onClick={() => setShowObjectModal(true)}
                      className="flex flex-1 items-center justify-center bg-headerBg text-xs hover:bg-gray-700 rounded-3xl"
                    >
                      <span className="flex items-center gap-2">
                        <Plus className="size-3" />
                        객체 생성
                      </span>
                    </Button>
                    
                    <Button
                      variant="danger"
                      onClick={() => setShowUploadModal(true)}
                      className="flex flex-1 items-center justify-center text-xs rounded-3xl"
                    >
                      <span className="flex items-center gap-2">
                        <Upload className="size-3" />
                        파일 업로드
                      </span>
                    </Button>
                    
                    <Button
                      variant={bulkMode ? 'primary' : 'secondary'}
                      onClick={toggleBulkMode}
                      className={`flex flex-1 items-center justify-center text-xs rounded-3xl ${bulkMode ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    >
                      <span className="flex items-center gap-2">
                        <Settings className="size-3" />
                        {bulkMode ? '대량 작업' : '대량 작업'}
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* 컨텐츠 영역 - flex 컨테이너로 변경 */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* 스크롤 가능한 테이블 영역 */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* 대량 작업 패널 */}
                {bulkMode && (
                  <div className="mb-4 rounded-3xl border border-orange-200 bg-orange-50 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className={`font-semibold text-orange-800 ${isMobileView ? 'text-xs' : 'text-sm'}`}>
                          대량 작업 모드 ({selectedExams.length}개 선택됨)
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleSelectAll}
                          className="text-orange-600 hover:text-orange-800 rounded-3xl"
                        >
                          {selectedExams.length === examData.length ? '전체 해제' : '전체 선택'}
                        </Button>
                      </div>
                      <Button
                        variant="primary"
                        onClick={handleBulkAnalyze}
                        disabled={selectedExams.length === 0}
                        className="bg-orange-500 hover:bg-orange-600 rounded-3xl"
                      >
                        선택 항목 분석 시작
                      </Button>
                    </div>
                  </div>
                )}

                {/* 데이터 테이블 */}
                {isLoading ? (
                  <div className="py-4 text-center">로딩 중...</div>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {bulkMode && (
                          <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleSelectAll}
                              className="p-0"
                            >
                              {selectedExams.length === examData.length ? 
                                <CheckSquare className="size-3" /> : 
                                <Square className="size-3" />
                              }
                            </Button>
                          </th>
                        )}
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>ID</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>학교명</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>출제년도</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>학년</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>시험구분</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>과목</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>파일</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>분석</th>
                        <th className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}>관리</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {examData.map((exam) => {
                        const phaseLabel = phases.find(p => p.value == exam.phase)?.label || exam.phase;
                        
                        return (
                          <tr 
                            key={exam.sxm_id} 
                            className={`group cursor-pointer transition-all duration-200 ${
                              !exam.file_exists ? 'bg-red-50' : ''
                            }`}
                            onClick={(e) => {
                              // 버튼이나 체크박스 클릭 시에는 미리보기 모달을 열지 않음
                              if (e.target.closest('button') || e.target.closest('[role="button"]')) {
                                return;
                              }
                              handleViewDetail(exam);
                            }}
                          >
                            {bulkMode && (
                              <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleExamSelect(exam.sxm_id)}
                                  className="p-0"
                                >
                                  {selectedExams.includes(exam.sxm_id) ? 
                                    <CheckSquare className="size-3 text-orange-500" /> : 
                                    <Square className="size-3 text-gray-400" />
                                  }
                                </Button>
                              </td>
                            )}
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center font-mono text-sm text-gray-900 whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>{exam.sxm_id}</td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-sm text-gray-900 whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>{getDisplaySchoolName(exam.school_name)}</td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-sm text-gray-900 whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>{exam.att_year}</td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-sm text-gray-900 whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>{formatGradeDisplay(exam.school_grade)}</td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-sm text-gray-900 whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>{phaseLabel}</td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center text-sm text-gray-900 whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>{exam.subject || '-'}</td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>
                              {exam.file_exists ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  있음
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <AlertCircle className="mr-0.5 size-3" />
                                  없음
                                </span>
                              )}
                            </td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>
                              {getAnalysisStatusDisplay(exam.analysis)}
                            </td>
                            <td className={`${isMobileView ? 'px-2' : 'px-3'} py-2 text-center whitespace-nowrap group-hover:bg-green-50 first:group-hover:rounded-l-3xl last:group-hover:rounded-r-3xl`}>
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDetail(exam)}
                                  className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                  title="분석 정보"
                                >
                                  <Eye className="size-4" />
                                </Button>
                                {exam.file_exists ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleAnalyze(exam.sxm_id, exam)}
                                      className={`p-1 rounded ${canRequestAnalysis(exam.analysis) ? 'text-success hover:text-green-700 hover:bg-green-50' : 'cursor-not-allowed text-gray-300'}`}
                                      title={canRequestAnalysis(exam.analysis) ? "분석 시작" : "분석 요청 불가"}
                                      disabled={!canRequestAnalysis(exam.analysis)}
                                    >
                                      <Bot className="size-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleFileDelete(exam.sxm_id)}
                                      className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded"
                                      title="파일 삭제"
                                    >
                                      <FileX className="size-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSingleUpload(exam)}
                                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                    title="파일 업로드"
                                  >
                                    <UploadCloud className="size-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditObject(exam)}
                                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded"
                                  title="시험 객체 수정"
                                >
                                  <Edit className="size-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleObjectDelete(exam.sxm_id, exam)}
                                  className={`p-1 rounded ${canDeleteObject(exam.analysis) ? 'text-danger hover:text-red-700 hover:bg-red-50' : 'cursor-not-allowed text-gray-300'}`}
                                  title={canDeleteObject(exam.analysis) ? "시험 객체 삭제" : "삭제 불가"}
                                  disabled={!canDeleteObject(exam.analysis)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}

                {!isLoading && examData.length === 0 && (
                  <div className="py-8 text-center text-gray-500">
                    데이터가 없습니다.
                  </div>
                )}
              </div>

              {/* 페이지네이션 - 고정된 위치 */}
              {!isLoading && examData.length > 0 && (
                <div className="shrink-0 bg-white" style={{ paddingBottom: isMobileView ? '12px' : '16px', paddingLeft: isMobileView ? '12px' : '16px', paddingRight: isMobileView ? '12px' : '16px' }}>
                  <div className={isMobileView ? "flex justify-center items-center gap-2" : "desktop-pagination"}>
                    {/* 이전 버튼 */}
                    <button 
                      className={isMobileView ? "w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" : "page-button"}
                      style={!isMobileView ? { borderRadius: '1.5rem' } : {}}
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      aria-label="이전 페이지"
                    >
                      {isMobileView ? '<' : <ChevronLeft size={16} className="font-bold" />}
                    </button>
                    
                    {/* 페이지 번호들 */}
                    <div className="flex gap-1">
                      {renderPageNumbersForMobile()}
                    </div>
                    
                    {/* 다음 버튼 */}
                    <button 
                      className={isMobileView ? "w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" : "page-button"}
                      style={!isMobileView ? { borderRadius: '1.5rem' } : {}}
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}
                      aria-label="다음 페이지"
                    >
                      {isMobileView ? '>' : <ChevronRight size={16} className="font-bold" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 분석 결과 탭 */}
        {activeTab === 'analysis' && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <AnalysisResultTable />
          </div>
        )}
      </div>

          {/* 모든 모달들을 BypassLayout 내부로 이동 */}
          
          {/* 단일 파일 업로드 모달 */}
          <SingleFileUploadModal
            isOpen={showSingleUploadModal}
            onClose={() => {
              setShowSingleUploadModal(false);
              setSelectedExamForUpload(null);
            }}
            exam={selectedExamForUpload}
            onSuccess={handleSingleUploadSuccess}
          />

          {/* 객체 수정 모달 */}
          <EditObjectModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedExamForEdit(null);
            }}
            exam={selectedExamForEdit}
            onSuccess={handleEditSuccess}
          />

          {/* 시험 객체 생성/수정 모달 */}
          <ObjectModal
            isOpen={showObjectModal}
            onClose={() => setShowObjectModal(false)}
            onSuccess={handleObjectSuccess}
          />

          {/* 업로드 모달 */}
          <UploadModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onUploadSuccess={handleUploadSuccess}
          />

          

          {/* 시험 상세 정보 및 PDF 미리보기 통합 모달 */}
          {showDetailModal && selectedExamDetail && (
            <Modal
              isOpen={showDetailModal}
              onClose={() => {
                setShowDetailModal(false);
                setSelectedExamDetail(null);
                cancelEditProblem(); // 편집 상태 초기화
              }}
              title="분석 정보"
              size="full"
              className="max-w-[1400px]"
              bodyClassName="p-0 flex flex-col overflow-hidden"
              isMobileView={isMobileView}
              zIndex={9999950}
              preventScroll={true}
            >
              {/* 개념 선택 드롭다운 - Portal로 body에 렌더링 */}
              {showConceptSelector && editingProblem && createPortal(
                <>
                  {/* 드롭다운 외부 클릭 감지용 오버레이 */}
                  <div 
                    className="fixed inset-0 z-[9999959]" 
                    onClick={() => setShowConceptSelector(false)}
                  />
                  <div 
                    className={`fixed bg-white border-gray-300 p-2 w-72 z-[9999960] ${
                      conceptSelectorPosition.showAbove 
                        ? 'rounded-t-lg rounded-b-none border border-b-0 shadow-lg' 
                        : 'rounded-b-lg rounded-t-none border border-t-0 shadow-lg'
                    }`}
                    style={{
                      top: `${conceptSelectorPosition.top}px`,
                      left: `${conceptSelectorPosition.left}px`,
                      maxHeight: '300px'
                    }}

                  >
                  <input
                    type="text"
                    placeholder="개념 검색..."
                    value={conceptSearchTerm}
                    onChange={(e) => setConceptSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 text-xs border rounded mb-2"
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <div className="overflow-y-auto" style={{ maxHeight: '250px' }}>
                    {isLoadingConceptList ? (
                      <div className="text-xs text-gray-500 p-2">로딩 중...</div>
                    ) : allMathConcepts.length === 0 ? (
                      <div className="text-xs text-gray-500 p-2">개념 목록을 불러오는 중...</div>
                    ) : (
                      allMathConcepts
                        .filter(c => {
                          // 검색어가 없으면 전체 표시
                          if (!conceptSearchTerm) return true;
                          
                          return c.concept_name.toLowerCase().includes(conceptSearchTerm.toLowerCase()) ||
                                 c.concept_id.toString().includes(conceptSearchTerm) ||
                                 c.order?.toString().includes(conceptSearchTerm);
                        })
                        .map(concept => {
                          // concept_type에 따른 색상 매핑 (테이블과 동일)
                          const getConceptTypeColor = (type) => {
                            const colors = {
                              'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
                              'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
                              'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
                              'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
                              'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
                              'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
                              'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
                              'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
                            };
                            return colors[type] || 'rgba(156, 163, 175, 0.5)'; // 기본 회색
                          };
                          
                          return (
                            <div
                              key={concept.concept_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleConceptSelection(concept.concept_id);
                              }}
                              className={`px-2 py-1 text-xs cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                                editingConceptIds.includes(concept.concept_id) ? 'bg-blue-100' : ''
                              }`}
                            >
                              {/* Order 뱃지 */}
                              <span 
                                className="inline-flex items-center justify-center font-semibold text-gray-700"
                                style={{
                                  backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                  borderRadius: '6px',
                                  width: '32px',
                                  height: '20px',
                                  minWidth: '32px',
                                  fontSize: '10px'
                                }}
                              >
                                {concept.order}
                              </span>
                              
                              {/* Type 뱃지 */}
                              <span 
                                className="inline-flex items-center justify-center font-semibold text-gray-800"
                                style={{
                                  backgroundColor: getConceptTypeColor(concept.concept_type),
                                  borderRadius: '6px',
                                  width: '32px',
                                  height: '20px',
                                  minWidth: '32px',
                                  fontSize: '9px'
                                }}
                                title={
                                  concept.concept_type === 'DEF' ? '정의' :
                                  concept.concept_type === 'AXM' ? '공리' :
                                  concept.concept_type === 'IDN' ? '정리' :
                                  concept.concept_type === 'THM' ? '이론' :
                                  concept.concept_type === 'MTH' ? '방법론' :
                                  concept.concept_type === 'PRP' ? '성질' :
                                  concept.concept_type === 'FOR' ? '공식' :
                                  concept.concept_type === 'NTN' ? '표기법' : ''
                                }
                              >
                                {concept.concept_type}
                              </span>
                              
                              {/* 개념명 */}
                              <span className="flex-1" dangerouslySetInnerHTML={{ __html: renderLatex(concept.concept_name) }} />
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
                </>,
                document.body
              )}
              
              {/* 시험 기본 정보 */}
              <div className="shrink-0 border-b bg-gray-50 p-4">
                <div className={`text-gray-700 ${isMobileView ? 'text-xs' : 'text-xs'}`} style={{ lineHeight: '1.5', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: '7.2px' }}>
                  <span><strong>ID:</strong> {selectedExamDetail.sxm_id}</span>
                  <span><strong>출제년도:</strong> {selectedExamDetail.att_year}년</span>
                  <span><strong>교육과정:</strong> {selectedExamDetail.edu_version ? `${selectedExamDetail.edu_version} 개정` : '없음'}</span>
                  
                  <span><strong>학교:</strong> {selectedExamDetail.school_name}</span>
                  <span><strong>학년:</strong> {formatGradeDisplay(selectedExamDetail.school_grade)}</span>
                  <span><strong>시험구분:</strong> {phases.find(p => p.value == selectedExamDetail.phase)?.label || selectedExamDetail.phase}</span>
                  
                  <span><strong>과목:</strong> {selectedExamDetail.subject || '없음'}</span>
                  <span><strong>파일:</strong> {selectedExamDetail.file_exists ? '있음' : '없음'}</span>
                  <span><strong>상태:</strong> {selectedExamDetail.is_closed ? '닫힘' : '열림'}</span>
                </div>
              </div>

              {/* 메인 컨텐츠 */}
              <div
                className={`${isMobileView ? 'overflow-hidden' : 'flex'}`}
                style={{
                  height: isMobileView
                    ? '100%'
                    : 'calc(90vh - 230px)',
                  flex: isMobileView ? 1 : undefined,
                  display: isMobileView ? 'grid' : undefined,
                  gridTemplateRows: isMobileView ? '1fr 1fr' : undefined
                }}
              >
                {/* PDF 미리보기 */}
                <div
                  className={`border-r ${isMobileView ? 'border-b border-r-0 overflow-hidden' : 'w-2/3 flex-none'}`}
                  style={isMobileView ? {} : {}}
                >
                  {selectedExamDetail.fileUrl ? (
                    <div 
                      className="relative size-full bg-gray-100"
                      style={{
                        ...(isMobileView && {
                          touchAction: 'manipulation',
                          overflow: 'auto',
                          WebkitOverflowScrolling: 'touch',
                          position: 'relative'
                        })
                      }}
                    >
                      {/* 상단 버튼 - 모든 플랫폼 */}
                      <div className="absolute right-2 top-2 z-10 flex gap-2">
                        <button
                          onClick={() => window.open(selectedExamDetail.fileUrl, '_blank')}
                          className={`${isMobileView ? 'px-2 py-1 text-xs' : 'px-2 py-1 text-xs'} touch-manipulation rounded-3xl bg-black bg-opacity-60 text-white backdrop-blur-sm transition-all hover:bg-opacity-80`}
                          title="새 탭에서 열기"
                        >
                          새탭
                        </button>
                        <button
                          onClick={() => window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(selectedExamDetail.fileUrl)}`, '_blank')}
                          className={`${isMobileView ? 'px-2 py-1 text-xs' : 'px-2 py-1 text-xs'} touch-manipulation rounded-3xl bg-black bg-opacity-60 text-white backdrop-blur-sm transition-all hover:bg-opacity-80`}
                          title="전체화면으로 보기"
                        >
                          전체화면
                        </button>
                      </div>
                      
                      {/* PDF 뷰어 - 모든 플랫폼에서 Google Docs Viewer 사용 */}
                      <iframe
                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedExamDetail.fileUrl)}&embedded=true`}
                        className="size-full border-0"
                        title="PDF 미리보기"
                        style={{ 
                          backgroundColor: '#ffffff',
                          ...(isMobileView && {
                            touchAction: 'manipulation',
                            WebkitOverflowScrolling: 'touch'
                          })
                        }}
                        allow="fullscreen"
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                      />
                    </div>
                  ) : selectedExamDetail.fileError ? (
                    <div className="flex h-full flex-col items-center justify-center bg-red-50 p-4">
                      <div className="mb-2 text-center text-red-600">
                        <AlertCircle className="mx-auto mb-2 size-8" />
                        파일 로딩 실패
                      </div>
                      <div className="text-center text-sm text-red-500">
                        {selectedExamDetail.fileError}
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleViewDetail({ sxm_id: selectedExamDetail.sxm_id, file_exists: selectedExamDetail.file_exists })}
                        className="mt-3"
                      >
                        다시 시도
                      </Button>
                    </div>
                  ) : selectedExamDetail.file_exists ? (
                    <div className="flex h-full items-center justify-center bg-gray-50">
                      <div className="text-gray-500">파일 로딩 중...</div>
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center bg-gray-50">
                      <div className="text-gray-500">업로드된 파일이 없습니다.</div>
                    </div>
                  )}
                </div>

                                {/* 분석 정보 */}
                <div
                  className={`flex flex-col ${isMobileView ? 'overflow-hidden' : ''}`}
                  style={isMobileView ? {} : { flex: 1 }}
                >
                  <div 
                    className="flex flex-col overflow-y-auto pt-2" 
                    style={{ height: '100%' }}
                  >
                    {selectedExamDetail.analysis && Object.keys(selectedExamDetail.analysis).length > 0 ? (
                      <div className="flex min-h-0 flex-1 flex-col space-y-2">
                        
                        {/* 분석 결과 */}
                        {selectedExamDetail.analysis.analysis_result && (
                          <div className="flex min-h-0 flex-1 flex-col">
                            {(() => {
                              try {
                                const analysisData = typeof selectedExamDetail.analysis.analysis_result === 'string' 
                                  ? JSON.parse(selectedExamDetail.analysis.analysis_result)
                                  : selectedExamDetail.analysis.analysis_result;
                                
                                if (Array.isArray(analysisData) && analysisData.length > 0) {
                                  return (
                                    <>
                                      <div className="flex items-center justify-between mb-1 shrink-0">
                                        <h4 className={`font-semibold ${isMobileView ? 'text-sm' : 'text-base'}`}>
                                          분석 결과 ({analysisData.length}개 문제)
                                        </h4>
                                        <button
                                          onClick={() => copyTableToExcel(analysisData)}
                                          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded-3xl hover:bg-blue-600 transition-colors"
                                          title="테이블을 엑셀로 복사"
                                        >
                                          <Copy className="w-3 h-3" />
                                          복사
                                        </button>
                                      </div>
                                      <div className="flex-1 overflow-y-auto rounded border bg-white">
                                          <style>
                                            {`
                                              .katex-concept .katex {
                                                font-size: 0.7rem !important;
                                              }
                                              .katex-concept .katex .base {
                                                line-height: 1.1;
                                              }
                                            `}
                                          </style>
                                          <table className="w-full text-xs">
                                            <thead className="sticky top-0 z-30 bg-gray-50">
                                              <tr>
                                                <th className="w-12 border-b px-2 py-1 text-left font-semibold">문제</th>
                                                <th className="w-12 border-b px-2 py-1 text-left font-semibold">난이도</th>
                                                <th className="border-b px-2 py-1 text-left font-semibold">매칭된 개념</th>
                                                <th className="w-16 border-b px-2 py-1 text-center font-semibold">수정</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {analysisData.map((problem, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                  <td className="border-b px-2 py-1 text-center font-medium">
                                                    {problem.problem_number || '-'}
                                                  </td>
                                                                                                      <td className="border-b px-2 py-1 text-center">
                                                    {editingProblem === problem.problem_number ? (
                                                      <select
                                                        value={editingDifficulty}
                                                        onChange={(e) => setEditingDifficulty(parseInt(e.target.value))}
                                                        className="w-16 px-1 py-0.5 text-xs border rounded"
                                                        onClick={(e) => e.stopPropagation()}
                                                      >
                                                        <option value={1}>1</option>
                                                        <option value={2}>2</option>
                                                        <option value={3}>3</option>
                                                        <option value={4}>4</option>
                                                        <option value={5}>5</option>
                                                      </select>
                                                    ) : problem.difficulty ? (
                                                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                                                        problem.difficulty === 1 ? 'bg-yellow-100 text-yellow-600' :
                                                        problem.difficulty === 2 ? 'bg-green-100 text-green-600' :
                                                        problem.difficulty === 3 ? 'bg-blue-100 text-blue-600' :
                                                        problem.difficulty === 4 ? 'bg-orange-100 text-orange-600' :
                                                        problem.difficulty === 5 ? 'bg-red-100 text-red-600' :
                                                        'bg-gray-100 text-gray-600'
                                                      }`}>
                                                        {problem.difficulty}
                                                      </span>
                                                    ) : (
                                                      <span className="rounded bg-gray-100 px-1 py-0.5 text-xs font-medium text-gray-400">
                                                        -
                                                      </span>
                                                    )}
                                                  </td>
                                                                                                      <td className="border-b px-2 py-1">
                                                    {editingProblem === problem.problem_number ? (
                                                      <div className="space-y-1">
                                                        {/* 선택된 개념들 표시 */}
                                                        <div className="space-y-1 max-h-32 overflow-y-auto">
                                                          {editingConceptIds.length > 0 ? (
                                                            editingConceptIds.map((conceptId, idx) => {
                                                              const concept = allMathConcepts.find(c => c.concept_id === conceptId);
                                                              return (
                                                                <div key={idx} className="flex items-center gap-1 text-xs bg-blue-50 rounded px-1 py-0.5">
                                                                  {/* eslint-disable-next-line react/no-danger -- katex 출력(개념 사전 값) */}
                                                                  <span className="flex-1" dangerouslySetInnerHTML={{ __html: concept ? renderLatex(concept.concept_name) : `개념 ${conceptId}` }} />
                                                                  <button
                                                                    onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      toggleConceptSelection(conceptId);
                                                                    }}
                                                                    className="text-red-500 hover:text-red-700"
                                                                  >
                                                                    <X className="w-3 h-3" />
                                                                  </button>
                                                                </div>
                                                              );
                                                            })
                                                          ) : (
                                                            <div className="text-xs text-gray-400">개념을 선택해주세요</div>
                                                          )}
                                                        </div>
                                                        
                                                        {/* 개념 추가 버튼 */}
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            const buttonRect = e.currentTarget.getBoundingClientRect();
                                                            
                                                            // 드롭다운 높이와 너비
                                                            const dropdownHeight = 300;
                                                            const dropdownWidth = 288; // w-72 = 18rem = 288px
                                                            
                                                            // 화면 기준 공간 확인
                                                            const spaceAbove = buttonRect.top;
                                                            const spaceBelow = window.innerHeight - buttonRect.bottom;
                                                            
                                                            // 위치 결정
                                                            let topPosition;
                                                            let showAbove = true;
                                                            
                                                            if (spaceAbove >= dropdownHeight) {
                                                              // 위쪽에 충분한 공간이 있으면 위에 표시
                                                              topPosition = buttonRect.top - dropdownHeight;
                                                            } else if (spaceBelow >= dropdownHeight) {
                                                              // 위쪽 공간이 부족하면 아래에 표시
                                                              topPosition = buttonRect.bottom;
                                                              showAbove = false;
                                                            } else {
                                                              // 둘 다 부족하면 위쪽에 최대한 표시
                                                              topPosition = Math.max(10, buttonRect.top - dropdownHeight);
                                                            }
                                                            
                                                            // 드롭다운이 버튼 너비를 포함하도록 위치 조정
                                                            let leftPosition = buttonRect.left;
                                                            
                                                            // 화면 오른쪽을 벗어나지 않도록 조정
                                                            if (leftPosition + dropdownWidth > window.innerWidth) {
                                                              leftPosition = window.innerWidth - dropdownWidth - 10;
                                                            }
                                                            
                                                            setConceptSelectorPosition({
                                                              top: topPosition,
                                                              left: leftPosition,
                                                              showAbove: showAbove,
                                                              buttonWidth: buttonRect.width
                                                            });
                                                            setShowConceptSelector(!showConceptSelector);
                                                          }}
                                                          className={`text-xs transition-all ${
                                                            showConceptSelector 
                                                              ? `text-blue-800 bg-white px-2 py-1 border relative z-50 ${
                                                                  conceptSelectorPosition?.showAbove 
                                                                    ? 'rounded-b border-t-transparent border-l-gray-300 border-r-gray-300 border-b-gray-300' 
                                                                    : 'rounded-t border-b-transparent border-l-gray-300 border-r-gray-300 border-t-gray-300'
                                                                }` 
                                                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                                          }`}
                                                        >
                                                          + 개념 추가
                                                        </button>
                                                      </div>
                                                    ) : (
                                                      <div className="space-y-1">
                                                        {Array.isArray(problem.matched_concept_ids) && problem.matched_concept_ids.length > 0 ? (
                                                          problem.matched_concept_ids.map((conceptId, conceptIndex) => {
                                                          const conceptText = getConceptText(conceptId);
                                                          const renderedText = renderLatex(conceptText);
                                                          const conceptOrder = getConceptOrder(conceptId);
                                                          const conceptData = getConceptById(conceptId);
                                                          
                                                          // concept_type에 따른 색상 매핑 (무지개색 50% 투명도)
                                                          const getConceptTypeColor = (type) => {
                                                            const colors = {
                                                              'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
                                                              'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
                                                              'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
                                                              'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
                                                              'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
                                                              'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
                                                              'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
                                                              'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
                                                            };
                                                            return colors[type] || 'rgba(156, 163, 175, 0.5)'; // 기본 회색
                                                          };
                                                          
                                                          return (
                                                            <div key={conceptIndex} className="flex items-center gap-2 text-xs text-gray-700 mb-1">
                                                              {/* Order 뱃지 - 연한 회색 */}
                                                              <span 
                                                                className="inline-flex items-center justify-center font-semibold text-gray-700 cursor-pointer hover:bg-gray-400"
                                                                style={{
                                                                  backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                                                  borderRadius: '6px',
                                                                  width: '32px',
                                                                  height: '20px',
                                                                  minWidth: '32px',
                                                                  fontSize: '10px',
                                                                  transition: 'background-color 0.2s'
                                                                }}
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  fetchMathceptDetails(conceptId);
                                                                }}
                                                                title="클릭하여 상세 정보 보기"
                                                              >
                                                                {conceptOrder}
                                                              </span>
                                                              
                                                              {/* Concept Type 뱃지 - 무지개색 */}
                                                              {conceptData && conceptData.concept_type && (
                                                                <span 
                                                                  className="inline-flex items-center justify-center font-semibold text-gray-800"
                                                                  style={{
                                                                    backgroundColor: getConceptTypeColor(conceptData.concept_type),
                                                                    borderRadius: '6px',
                                                                    width: '32px',
                                                                    height: '20px',
                                                                    minWidth: '32px',
                                                                    fontSize: '9px'
                                                                  }}
                                                                  title={
                                                                    conceptData.concept_type === 'DEF' ? '정의' :
                                                                    conceptData.concept_type === 'AXM' ? '공리' :
                                                                    conceptData.concept_type === 'IDN' ? '정리' :
                                                                    conceptData.concept_type === 'THM' ? '이론' :
                                                                    conceptData.concept_type === 'MTH' ? '방법론' :
                                                                    conceptData.concept_type === 'PRP' ? '성질' :
                                                                    conceptData.concept_type === 'FOR' ? '공식' :
                                                                    conceptData.concept_type === 'NTN' ? '표기법' : ''
                                                                  }
                                                                >
                                                                  {conceptData.concept_type}
                                                                </span>
                                                              )}
                                                              
                                                              {/* 개념 텍스트 */}
                                                              <span 
                                                                className="katex-concept flex-1"
                                                                dangerouslySetInnerHTML={{ __html: renderedText }}
                                                              />
                                                            </div>
                                                          );
                                                        })
                                                      ) : (
                                                        <div className="text-center text-xs text-gray-400">
                                                          매칭된 개념 없음
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                  </td>
                                                  <td className="border-b px-2 py-1 text-center">
                                                    {editingProblem === problem.problem_number ? (
                                                      <div className="flex justify-center gap-1">
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            saveProblemAnalysis(selectedExamDetail.sxm_id, problem.problem_number);
                                                          }}
                                                          className="p-1 text-green-600 hover:text-green-800"
                                                          title="저장"
                                                        >
                                                          <Save className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            cancelEditProblem();
                                                          }}
                                                          className="p-1 text-red-600 hover:text-red-800"
                                                          title="취소"
                                                        >
                                                          <X className="w-3 h-3" />
                                                        </button>
                                                      </div>
                                                    ) : (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          startEditProblem(problem);
                                                        }}
                                                        className="p-1 text-blue-600 hover:text-blue-800"
                                                        title="수정"
                                                      >
                                                        <Edit2 className="w-3 h-3" />
                                                      </button>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </>
                                    );
                                  } else {
                                    return (
                                      <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                                        분석 결과가 없습니다.
                                      </div>
                                    );
                                  }
                                } catch (error) {
                                  console.error('Error parsing analysis result:', error);
                                  return (
                                    <div className="flex min-h-0 flex-1 flex-col">
                                      <h4 className={`mb-2 shrink-0 font-semibold ${isMobileView ? 'text-sm' : 'text-base'}`}>
                                        분석 결과 (원본 데이터)
                                      </h4>
                                      <div className="min-h-0 flex-1 overflow-y-auto rounded border bg-white p-2">
                                        <pre className="whitespace-pre-wrap text-xs text-gray-700">
                                          {typeof selectedExamDetail.analysis.analysis_result === 'string' 
                                            ? selectedExamDetail.analysis.analysis_result 
                                            : JSON.stringify(selectedExamDetail.analysis.analysis_result, null, 2)}
                                        </pre>
                                      </div>
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          )}
                          
                          {/* 분석 결과가 없는 경우 */}
                          {!selectedExamDetail.analysis.analysis_result && (
                            <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                              분석 결과가 없습니다.
                            </div>
                          )}
                        </div>
                    ) : (
                      <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                        분석 정보가 없습니다.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Modal>
          )}
          
          {/* 매스컨셉 상세 정보 모달 - 공통 컴포넌트 사용 */}
          <MathceptDetailModal
            isOpen={mathceptDetailModalOpen}
            onClose={closeMathceptDetailModal}
            data={mathceptDetailData}
            loading={mathceptDetailLoading}
            renderLatex={renderLatex}
            showCloseButton={true}
            useLoader={false}
            handleAuth={false}
          />
        </div>
      )}
    />
  );
}

export default SchoolExamManagement;